<template>
  <div class="documents-page-container">
    <v-row>
      <v-col cols="12" md="6" v-for="card in ['totalDocuments', 'totalStorage']" :key="card">
        <v-card>
          <v-card-title>{{ $t(card) }}</v-card-title>
          <v-card-text class="text-h4">
            <v-skeleton-loader v-if="loading" type="text" width="100px" />
            <template v-else>
              {{ card === 'totalDocuments' ? totalDocuments : formatStorage(totalStorage) }}
            </template>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="4">
        <v-card class="mb-4" style="height: 460px;">
          <v-card-title>{{ $t('documentsByTeamAndUser') }}</v-card-title>
          <v-card-text>
            <v-skeleton-loader v-if="loading" type="image" height="400px" />
            <TeamUserDocumentBarChart v-else 
              :documents="documents" 
              :teamsObj="teamsObj" 
              :usersObj="usersObj" />
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-skeleton-loader v-if="loading" type="image" height="400px" />
        <DocumentFileTypeBarChart v-else :documents="documents" />
      </v-col>
      <v-col cols="12" md="4">
        <v-skeleton-loader v-if="loading" type="image" height="400px" />
        <CountCreatedLineChart v-else 
          :documents="documents" 
          :teamsObj="teamsObj" 
          :usersObj="usersObj" />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="12">
        <v-card>
          <v-card-title class="pa-0">
            <v-container fluid>
              <v-row no-gutters align="center">
                <v-col cols="auto">
                  <v-tabs v-model="activeTab">
                    <v-tab value="all">{{ $t('documentRepository') }}</v-tab>
                    <v-tab value="latest">
                      {{ $t('latestDocuments') }}
                      <v-badge
                        :content="recentDocumentsCount"
                        :model-value="recentDocumentsCount > 0"
                        color="error"
                        inline
                        class="ml-2"
                      ></v-badge>
                    </v-tab>           
                  </v-tabs>
                </v-col>
                <v-spacer></v-spacer>
                <v-col cols="auto">
                  <v-text-field
                    v-if="activeTab === 'all'"
                    v-model="search"
                    hide-details
                    density="compact"
                    variant="outlined"
                    prepend-inner-icon="mdi-magnify"
                    class="mt-2 mb-2 search-field"
                  ></v-text-field>
                  <v-text-field
                    v-else
                    v-model="searchLatest"
                    hide-details
                    density="compact"
                    variant="outlined"
                    prepend-inner-icon="mdi-magnify"
                    class="mt-2 mb-2 search-field"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-container>
          </v-card-title>
          <v-card-text>
            <v-window v-model="activeTab">
              <v-window-item value="latest">               
                <v-skeleton-loader v-if="loading" type="list-item-three-line" :types="{ skeleton: 'list-item' }" :boilerplate="true" :tile="true" :loading="true" />
                <template v-else>
                  <v-data-table
                    :headers="allDocumentsHeaders"
                    :items="filteredLatestDocuments"
                    :items-per-page="20"
                    class="elevation-1"
                    :locale="$i18n.locale"
                    :no-data-text="$t('noData')"
                    :items-per-page-text="$t('itemsPerPage')"
                  >
                    <template v-slot:item="{ item }">
                      <tr>
                        <td @click="handleItemClick(item)" style="cursor: pointer;">
                          <v-icon :icon="item.isFolder ? 'mdi-folder' : 'mdi-file-document'" class="mr-2"></v-icon>
                          {{ item.displayName }}
                        </td>
                        <td>{{ item.fileType }}</td>
                        <td>{{ formatStorage(item.fileSize) }}</td>
                        <td>
                          <v-tooltip :text="formatDate(item.createdDate, true)" location="bottom">
                            <template v-slot:activator="{ props }">
                              <span v-bind="props">{{ formatTimeAgo(item.createdDate) }}</span>
                            </template>
                          </v-tooltip>
                        </td>
                        <td>
                          <v-tooltip :text="item.fullPath" location="top">
                            <template v-slot:activator="{ props }">
                              <div v-bind="props" class="d-flex align-center">
                                <v-icon icon="mdi-folder" size="small" class="mr-2"></v-icon>
                                <span>{{ getParentFolderName(item.parentFolderId) }}</span>
                              </div>
                            </template>
                          </v-tooltip>
                        </td>
                        <td>{{ getTeamName(item.createdByTeamId) }}</td>
                        <td>
                          <v-tooltip
                            :text="getUserName(item.createdByUserId)"
                            location="bottom"
                          >
                            <template v-slot:activator="{ props }">
                              <v-avatar
                                v-bind="props"
                                :size="28"
                                class="mr-1"
                              >
                                <v-img
                                  v-if="getUserAvatar(item.createdByUserId)"
                                  :src="'https://' + getUserAvatar(item.createdByUserId)"
                                  alt="User Avatar"
                                ></v-img>
                                <span
                                  v-else
                                  class="d-flex justify-center align-center text-caption white--text"
                                  :style="{
                                    backgroundColor: getRandomColor(item.createdByUserId),
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '50%'
                                  }"
                                >
                                  {{ getInitials(item.userDisplayName) }}
                                </span>
                              </v-avatar>
                            </template>
                          </v-tooltip>
                        </td>
                      </tr>
                    </template>
                  </v-data-table>
                </template>
              </v-window-item>
              <v-window-item value="all">            
                <v-skeleton-loader v-if="loading" type="list-item-three-line" :types="{ skeleton: 'list-item' }" :boilerplate="true" :tile="true" :loading="true" />
                <template v-else>
                  <v-breadcrumbs :items="breadcrumbs">
                    <template v-slot:item="{ item }">
                      <v-breadcrumbs-item
                        :title="item.title"
                        :disabled="item.disabled"
                        :href="item.href"
                        @click.prevent="goToPath(item)"
                      >
                        {{ item.title }}
                      </v-breadcrumbs-item>
                    </template>
                  </v-breadcrumbs>

                  <v-data-table
                    :headers="headers"
                    :items="filteredDocuments"
                    :items-per-page="20"
                    class="elevation-1"
                    :locale="$i18n.locale"
                    :no-data-text="$t('noData')"
                    :items-per-page-text="$t('itemsPerPage')"
                  >
                    <template v-slot:item="{ item }">
                      <tr>
                        <td @click="handleItemClick(item)" style="cursor: pointer;">
                          <v-icon :icon="item.isFolder ? 'mdi-folder' : 'mdi-file-document'" class="mr-2"></v-icon>
                          {{ item.displayName }}
                        </td>
                        <td>{{ item.fileType }}</td>
                        <td>{{ formatStorage(item.fileSize) }}</td>
                        <td>
                          <v-tooltip :text="formatDate(item.createdDate, true)" location="bottom">
                            <template v-slot:activator="{ props }">
                              <span v-bind="props">{{ formatDate(item.createdDate) }}</span>
                            </template>
                          </v-tooltip>
                        </td>
                        <td>{{ getTeamName(item.createdByTeamId) }}</td>
                        <td>
                          <v-tooltip
                            :text="getUserName(item.createdByUserId)"
                            location="bottom"
                          >
                            <template v-slot:activator="{ props }">
                              <v-avatar
                                v-bind="props"
                                :size="28"
                                class="mr-1"
                              >
                                <v-img
                                  v-if="getUserAvatar(item.createdByUserId)"
                                  :src="'https://' + getUserAvatar(item.createdByUserId)"
                                  alt="User Avatar"
                                ></v-img>
                                <span
                                  v-else
                                  class="d-flex justify-center align-center text-caption white--text"
                                  :style="{
                                    backgroundColor: getRandomColor(item.createdByUserId),
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '50%'
                                  }"
                                >
                                  {{ getInitials(item.userDisplayName) }}
                                </span>
                              </v-avatar>
                            </template>
                          </v-tooltip>
                        </td>
                      </tr>
                    </template>
                  </v-data-table>
                </template>
              </v-window-item>
            </v-window>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, getCurrentInstance } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { format, formatDistanceToNow } from 'date-fns';
import DocumentFileTypeBarChart from './charts/DocumentFileTypeBarChart.vue';
import TeamUserDocumentBarChart from './charts/TeamUserDocumentBarChart.vue';
import CountCreatedLineChart from './charts/CountCreatedLineChart.vue';

const store = useStore();
const { t } = useI18n();
const settings = computed(() => store.state.settings);
const buildMode = computed(() => store.state.buildMode);

const teamsObj = computed(() => store.getters.getTeamsObj);
const usersObj = computed(() => store.getters.getUsersObj);

const documents = ref([]);
const currentFolderId = ref("");
const rootFolderId = ref("");
const loading = ref(true);
const search = ref('');
const searchLatest = ref('');
const activeTab = ref('all');

const allDocumentsHeaders = computed(() => [
      { title: t('name'), key: 'displayName' },
      { title: t('type'), key: 'fileType' },
      { title: t('size'), key: 'fileSize' },
      { title: t('date'), key: 'createdDate' },
      { title: t('folder'), key: 'parentFolderId' },
      { title: t('uploadedBy'), key: 'teamId' },
      { title: '', key: 'userId' }
    ]);

    const headers = computed(() => [
      { title: t('name'), key: 'displayName' },
      { title: t('type'), key: 'fileType' },
      { title: t('size'), key: 'fileSize' },
      { title: t('uploadedDate'), key: 'createdDate' },
      { title: t('uploadedBy'), key: 'teamId' },
      { title: '', key: 'userId' }
    ]);

const getData = async () => {
  try {
    loading.value = true;
    documents.value = await store.state.apiClient.get('/api/' + store.state.settings.projectCode + '/documents');
    // parentFolderId can come back as "", null or missing for top-level entries
    const rootFolder = documents.value.find(doc => doc.isFolder && !doc.parentFolderId);
    if (rootFolder) {
      rootFolderId.value = rootFolder.id;
      currentFolderId.value = rootFolder.id;
    }
  } catch (error) {
    console.error("Error fetching data:", error);
  } finally {
    loading.value = false;
  }
};

const filteredLatestDocuments = computed(() => {
  let docs = documents.value.filter(doc => !doc.isFolder).sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
  
  if (searchLatest.value) {
    const searchLower = searchLatest.value.toLowerCase();
    docs = docs.filter(item => {
      return Object.values(item).some(value => 
        String(value).toLowerCase().includes(searchLower)
      );
    });
  }
  
  return docs;
});

const filteredDocuments = computed(() => {
  if (!search.value) return currentFolderContent.value;

  const searchLower = search.value.toLowerCase();
  return documents.value.filter(item => {
    return Object.values(item).some(value => 
      String(value).toLowerCase().includes(searchLower)
    );
  });
});

const recentDocumentsCount = computed(() => {
  const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
  return documents.value.filter(doc => !doc.isFolder && new Date(doc.createdDate) > twentyFourHoursAgo).length;
});

watch(
  () => [
    settings.value.scopedToken, 
    settings.value.projectCode
  ],
  ([newScopedToken, newProjectCode]) => {
    if (newScopedToken && newProjectCode) {
      getData();
    }
  }
);

const totalDocuments = computed(() => documents.value.filter(doc => !doc.isFolder).length);

const totalStorage = computed(() => {
  return documents.value.reduce((total, doc) => total + doc.fileSize, 0);
});

const formatStorage = (bytes) => {
  if (bytes === undefined) {
   return 'N/A';
  } else if (bytes === 0) {
    return '-';
  }
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  return `${size.toFixed(2)} ${units[unitIndex]}`;
};

const currentFolderContent = computed(() => {
  if (search.value) return filteredDocuments.value;
  
  if (currentFolderId.value === "") {
    // No root folder record found: show all top-level entries, folders included,
    // so directories are still drillable
    return documents.value.filter(doc => !doc.parentFolderId);
  }
  return documents.value.filter(doc => doc.parentFolderId === currentFolderId.value);
});

const getParentFolderName = (parentFolderId) => {
  if (!parentFolderId) return t("Root");
  const parentFolder = documents.value.find(doc => doc.id === parentFolderId);
  return parentFolder ? parentFolder.displayName : "Unknown Folder";
};

const handleItemClick = (item) => {
  if (item.isFolder) {
    // An active search overrides the folder view, so drilling in must clear it
    // or the click has no visible effect
    search.value = '';
    currentFolderId.value = item.id;
  } else if (item.fileDownloadLink) {
    downloadFile(item);
  }
};

const downloadFile = (item) => {
  if (item.fileDownloadLink) {
    openUrl(item.fileDownloadLink);
  } else {
    console.warn('No download link available for this file.');
  }
};

const goToPath = (item) => {
  currentFolderId.value = item.id;
};

const breadcrumbs = computed(() => {
  const breadcrumbItems = [];
  let currentFolder = documents.value.find(doc => doc.id === currentFolderId.value);

  while (currentFolder && currentFolder.parentFolderId) {
    breadcrumbItems.unshift({
      title: currentFolder.displayName,
      disabled: false,
      href: 'javascript:void(0)',
      id: currentFolder.id
    });
    currentFolder = documents.value.find(doc => doc.id === currentFolder.parentFolderId);
  }

  breadcrumbItems.unshift({
    title: t('Root'),
    disabled: currentFolderId.value === "",
    href: 'javascript:void(0)',
    id: rootFolderId.value
  });

  if (breadcrumbItems.length > 0) {
    breadcrumbItems[breadcrumbItems.length - 1].disabled = true;
  }

  return breadcrumbItems;
});

const getTeamName = (teamId) => {
  return teamsObj.value[teamId] ? teamsObj.value[teamId].name : 'Unknown Team';
};

const getUserName = (userId) => {
  return usersObj.value[userId] ? usersObj.value[userId].displayName : 'Unknown User';
};

const getUserAvatar = (userId) => {
  return usersObj.value[userId] ? usersObj.value[userId].avatarUrl : null;
};

const getInitials = (name) => {
  if (!name) return '?';
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const getRandomColor = (id) => {
  const colors = ['#F44336', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5', '#2196F3', '#03A9F4', '#00BCD4', '#009688', '#4CAF50', '#8BC34A', '#CDDC39', '#FFC107', '#FF9800', '#FF5722'];
  const index = String(id || '?').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
  return colors[index];
};

const formatDate = (dateString, showTime = false) => {
  const date = new Date(dateString);
  return showTime
    ? format(date, 'yyyy-MM-dd HH:mm:ss')
    : format(date, 'yyyy-MM-dd');
};

const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  return formatDistanceToNow(date, { addSuffix: true });
};

const { proxy } = getCurrentInstance();
const openUrl = (url) => {
  if (buildMode.value === 'production') {
    proxy.$launchUrl(url);
  } else {
    window.open(url, '_blank');
  }
};

onMounted(() => {
  if (store.state.settings.projectCode) {
    getData();
  }
});
</script>

<style scoped>
.documents-page-container {
  max-width: 100%;
  /* clip (not hidden): hidden forces overflow-y to auto, creating a nested
     vertical scroll container inside the page scroller */
  overflow-x: clip;
}

.search-field {
  width: 300px;
}

@media (max-width: 600px) {
  .documents-page-container {
    max-width: 100vw;
  }

  .search-field {
    width: 200px;
  }
}

.v-card {
  height: 100%;
}
.chart {
  height: 400px;
}
</style>